import { useQueryClient } from "@tanstack/react-query"
import type { NodeType } from "@velizhanin-dev/bot-scenario"
import { ReactFlowProvider } from "@xyflow/react"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowLeft, Braces, ChevronDown, MessageSquareText, Power, Rocket, Zap } from "lucide-react"
import { type ReactNode, useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import type { NodeStat } from "@/entities/bot-builder"
import {
	botBuilderKeys,
	countErrorsByFlow,
	findFreePort,
	layoutFlow,
	placeNewNode,
	type ScenarioDraft,
} from "@/entities/bot-builder"
import {
	type DraftActions,
	FlowTriggersDialog,
	NodeInspector,
	useGraphIssues,
	VariablesDialog,
} from "@/features/edit-scenario-draft"
import { KIND_ORDER } from "@/features/manage-flows"
import { PublishDialog, VersionsDialog } from "@/features/publish-scenario"
import { TestChatPanel, useTestChat } from "@/features/test-scenario"
import { useDisableTmaSwipes } from "@/shared/hooks/use-disable-tma-swipes"
import { useIsDesktopEditor } from "@/shared/hooks/use-media-query"
import { TRANSITION } from "@/shared/lib/motion"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { Drawer, DrawerContent, DrawerTitle } from "@/shared/ui/drawer"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu"
import { Separator } from "@/shared/ui/separator"
import { LoadErrorState } from "@/shared/ui/load-error-state"
import { type RowAction } from "@/shared/ui/row-actions-menu"
import {
	BottomSheet,
	CanvasToolbar,
	FLOATING_PANEL_CLASS,
	FlowList,
	FlowsBar,
	type FocusRequest,
	IssuesMenu,
	ListToolbar,
	ScenarioCanvas,
} from "@/widgets/scenario-canvas"
import { type FlowView, MoreMenu, PANEL_CLASS, SaveIndicator, ViewSwitch } from "./editor-chrome"
import { ScenarioSettingsDialog } from "./scenario-settings-dialog"
import { LoadSwap } from "@/shared/ui/load-swap"

/**
 * Боковая панель раскрывается по ширине, и холст сужается вместе с ней, без рывка.
 * Отступ -8px съедает gap ряда, пока панель закрыта. Содержимое сразу полной ширины
 * (PanelBody), поэтому внутри ничего не перестраивается на ходу.
 */
const panelMotion = (width: number) => ({
	initial: { width: 0, marginLeft: -8, opacity: 0 },
	animate: { width, marginLeft: 0, opacity: 1, transition: TRANSITION.slow },
	exit: { width: 0, marginLeft: -8, opacity: 0, transition: TRANSITION.base },
})
const INSPECTOR_WIDTH = 380
const TEST_CHAT_WIDTH = 360

/** Редактор одной воронки внутри раздела бота (как «Воронка: …» в Botmaker). */
export interface EmbeddedFlowEditor {
	flowId: string
	onBack: () => void
	/** Блок из другой воронки: переход на её страницу. */
	onOpenFlow: (flowId: string, nodeId?: string) => void
	focusNodeId?: string
	/** «Прошли / Застряли» на блоках. */
	nodeStats?: ReadonlyMap<string, NodeStat>
	/** Шапка редактора: единственная на странице: слева кнопка меню раздела, справа публикация. */
	headerStart?: ReactNode
	headerEnd?: ReactNode
	/** Слева сверху на холсте: фильтр статистики. */
	canvasStart?: ReactNode
	/** Пункты меню «⋯» от страницы: например, «Пройти в Telegram». */
	menuExtra?: RowAction[]
}

export function Editor({
	draft,
	actions,
	isSaving,
	whenSaved,
	serverLoad,
	embedded,
}: {
	draft: ScenarioDraft
	actions: DraftActions
	isSaving: boolean
	whenSaved: () => Promise<void>
	serverLoad: number
	embedded?: EmbeddedFlowEditor
}) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [searchParams, setSearchParams] = useSearchParams()
	const graphIssues = useGraphIssues(draft)
	const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null)
	const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null)
	const [dialog, setDialog] = useState<"variables" | "settings" | "publish" | "versions" | null>(
		null,
	)
	const [triggersOpen, setTriggersOpen] = useState(false)
	const [testOpen, setTestOpen] = useState(false)
	const chat = useTestChat(draft.id, testOpen, whenSaved)
	const isDesktop = useIsDesktopEditor()
	useDisableTmaSwipes()
	// Телефону: список: на узком холсте блоки мелкие, а провода пальцем не протянуть.
	const [view, setView] = useState<FlowView>(() =>
		sessionStorage.getItem("kmk:scenario-view") === "canvas"
			? "canvas"
			: sessionStorage.getItem("kmk:scenario-view") === "list"
				? "list"
				: window.matchMedia("(min-width: 1024px)").matches
					? "canvas"
					: "list",
	)
	useEffect(() => {
		sessionStorage.setItem("kmk:scenario-view", view)
	}, [view])
	const showList = view === "list"

	const focusNodeId = embedded?.focusNodeId
	useEffect(() => {
		if (!focusNodeId) return
		setSelectedNodeId(focusNodeId)
		setFocusRequest((current) => ({ nodeId: focusNodeId, seq: (current?.seq ?? 0) + 1 }))
	}, [focusNodeId])

	const mainFlow = draft.flows.find((flow) => flow.kind === "MAIN") ?? draft.flows[0]
	const requestedFlow = draft.flows.find((flow) => flow.id === searchParams.get("flow"))
	const activeFlow = embedded
		? draft.flows.find((flow) => flow.id === embedded.flowId)
		: (requestedFlow ?? mainFlow)
	// Блок из другого потока не показываем, но и не сбрасываем: при переходе к блоку
	// выбор ставится раньше, чем роутер применит новый ?flow=.
	const selectedNode =
		draft.nodes.find((node) => node.id === selectedNodeId && node.flowId === activeFlow?.id) ?? null
	const selectionDeleted =
		!!selectedNodeId && !draft.nodes.some((node) => node.id === selectedNodeId)

	useEffect(() => {
		if (selectionDeleted) setSelectedNodeId(null)
	}, [selectionDeleted])

	if (!activeFlow) {
		return <LoadErrorState message={embedded ? "Воронка не найдена." : "В сценарии нет воронок."} />
	}

	const selectFlow = (flowId: string) => {
		setSelectedNodeId(null)
		if (embedded) embedded.onOpenFlow(flowId)
		else setSearchParams({ flow: flowId }, { replace: true })
	}

	const showNode = (nodeId: string) => {
		// После await в замыкании старый черновик: новый или перенесённый блок есть только в кэше.
		const latest =
			queryClient.getQueryData<ScenarioDraft>(botBuilderKeys.scenario(draft.id)) ?? draft
		const node = latest.nodes.find((item) => item.id === nodeId)
		if (!node) return
		if (node.flowId !== activeFlow.id) {
			if (embedded) {
				embedded.onOpenFlow(node.flowId, nodeId)
				return
			}
			setSearchParams({ flow: node.flowId }, { replace: true })
		}
		setSelectedNodeId(nodeId)
		setFocusRequest((current) => ({ nodeId, seq: (current?.seq ?? 0) + 1 }))
	}

	// Без выбранного блока новый цепляется к единственному свободному выходу воронки: так
	// цепочку можно собирать подряд, не выбирая каждый раз последний блок.
	const soleOpenEnd = useMemo(() => {
		if (selectedNode) return null
		const open = draft.nodes.filter(
			(node) => node.flowId === activeFlow.id && findFreePort(draft, node) !== null,
		)
		return open.length === 1 ? open[0]! : null
	}, [draft, activeFlow.id, selectedNode])

	/** `from`: конкретный выход, к которому подключить новый блок (строка выхода в списке). */
	const addBlock = async (
		type: Exclude<NodeType, "start">,
		from?: { nodeId: string; port: string },
	) => {
		const fromNode = from ? (draft.nodes.find((node) => node.id === from.nodeId) ?? null) : null
		const anchor = fromNode ?? selectedNode ?? soleOpenEnd
		const port = fromNode ? from!.port : anchor ? findFreePort(draft, anchor) : null
		const result = await actions.createNode({
			flowId: activeFlow.id,
			type,
			position: placeNewNode(draft, activeFlow.id, anchor),
			connectFrom: anchor && port ? { nodeId: anchor.id, port } : undefined,
		})
		if (result) showNode(result.node.id)
	}

	const arrange = () => {
		const positions = layoutFlow(draft, activeFlow.id)
		if (positions.length === 0) {
			toast.info("Блоки уже упорядочены")
			return
		}
		void actions.savePositions(positions)
	}

	const playFrom = (nodeId: string) => {
		// На телефоне тест-чат на весь экран: шторку блока закрываем, чтобы не перекрывала.
		if (!isDesktop) setSelectedNodeId(null)
		setTestOpen(true)
		void chat.send({ type: "play", nodeId })
	}

	const inspector = selectedNode ? (
		<NodeInspector
			key={`${selectedNode.id}:${serverLoad}`}
			draft={draft}
			node={selectedNode}
			issues={graphIssues.byNode.get(selectedNode.id) ?? []}
			actions={actions}
			onClose={() => setSelectedNodeId(null)}
			onSelectNode={showNode}
			onPlay={playFrom}
		/>
	) : null
	const testChat = (
		<TestChatPanel
			draft={draft}
			chat={chat}
			onClose={() => setTestOpen(false)}
			onShowNode={showNode}
		/>
	)

	const addBlockHint = selectedNode
		? findFreePort(draft, selectedNode)
			? "Новый блок подключится к свободному выходу выбранного"
			: "У выбранного блока все выходы заняты: блок добавится без связи"
		: soleOpenEnd
			? "Новый блок продолжит воронку с последнего свободного выхода"
			: "Выберите блок, чтобы новый сразу подключился к нему"

	const viewSwitch = <ViewSwitch view={view} onChange={setView} />
	const viewSwitchRow = <div className="max-sm:order-last max-sm:basis-full">{viewSwitch}</div>
	const testChatButton = (
		<Button
			variant={testOpen ? "secondary" : "ghost"}
			size="sm"
			className="h-9 rounded-xl"
			aria-pressed={testOpen}
			onClick={() => setTestOpen((open) => !open)}
		>
			<MessageSquareText />
			<span className="hidden sm:inline">Проверить бота</span>
		</Button>
	)
	const issuesMenu = (
		<IssuesMenu
			draft={draft}
			graphIssues={graphIssues}
			onPickNode={showNode}
			onPickFlow={selectFlow}
		/>
	)

	return (
		<ReactFlowProvider>
			<div
				className={cn(
					"flex flex-col bg-background",
					embedded ? "relative h-full" : "fixed inset-0 z-40",
				)}
			>
				{embedded ? (
					<header className="flex shrink-0 items-center gap-1.5 border-b border-border/60 px-2 pt-2 pb-2 max-sm:flex-wrap">
						{embedded.headerStart}
						{/* Та же ссылка назад, что на внутренних страницах бота: стрелка и название списка. */}
						<button
							type="button"
							aria-label="К списку воронок"
							onClick={embedded.onBack}
							className="inline-flex shrink-0 items-center gap-1.5 rounded-lg px-2 py-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							<ArrowLeft className="size-4" />
							<span className="hidden sm:inline">Воронки</span>
						</button>
						<Separator orientation="vertical" className="mx-0.5 h-8" />
						<div className="min-w-0 flex-1 px-1 sm:min-w-44">
							<p className="flex items-center gap-1.5 truncate text-[10px] leading-3 font-semibold tracking-[0.14em] text-primary uppercase">
								Воронка
								{activeFlow.kind === "CUSTOM" && activeFlow.active === false ? (
									<span className="font-medium tracking-normal text-amber-300 normal-case">
										выключена
									</span>
								) : null}
								<span className="font-normal tracking-normal text-muted-foreground normal-case">
									<SaveIndicator isSaving={isSaving} />
								</span>
							</p>
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<button
										type="button"
										title={`${activeFlow.name}: перейти к другой воронке`}
										className="flex max-w-full items-center gap-1 rounded-md text-left transition-colors hover:text-primary"
									>
										<span className="truncate text-sm leading-5 font-semibold">
											{activeFlow.name}
										</span>
										<ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
									</button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start" className="max-h-80 w-64 overflow-y-auto">
									{[...draft.flows]
										.sort(
											(a, b) =>
												KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.sortOrder - b.sortOrder,
										)
										.map((flow) => (
											<DropdownMenuItem
												key={flow.id}
												disabled={flow.id === activeFlow.id}
												className={cn(flow.active === false && "text-muted-foreground")}
												onSelect={() => embedded.onOpenFlow(flow.id)}
											>
												<span className="truncate">{flow.name}</span>
												{flow.kind === "MAIN" ? (
													<span className="ml-auto text-xs text-muted-foreground">основная</span>
												) : flow.active === false ? (
													<span className="ml-auto text-xs">выключена</span>
												) : null}
											</DropdownMenuItem>
										))}
								</DropdownMenuContent>
							</DropdownMenu>
						</div>
						{issuesMenu}
						{viewSwitchRow}
						{testChatButton}
						<MoreMenu
							onPick={setDialog}
							extra={[
								{
									label: "Запуск: команды и события",
									icon: Zap,
									hidden: activeFlow.kind === "FALLBACK",
									onSelect: () => setTriggersOpen(true),
								},
								{
									label: activeFlow.active === false ? "Включить воронку" : "Выключить воронку",
									icon: Power,
									hidden: activeFlow.kind !== "CUSTOM",
									onSelect: () =>
										void actions.setFlowActive(activeFlow.id, activeFlow.active === false),
								},
								...(embedded.menuExtra ?? []),
							]}
						/>
						{embedded.headerEnd}
					</header>
				) : (
					<header className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-2 pt-2 pb-2">
						<div className="flex min-w-0 items-center gap-1">
							<Button
								variant="ghost"
								size="icon"
								className="shrink-0 rounded-xl"
								aria-label="Назад"
								onClick={() =>
									// Сценарий открывают из бота: возвращаемся туда же, а по прямой ссылке к ботам.
									(window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate("/admin/bots")
								}
							>
								<ArrowLeft />
							</Button>
							<Button
								type="button"
								variant="ghost"
								title="Настройки сценария"
								onClick={() => setDialog("settings")}
								className="h-auto min-w-0 flex-col items-start justify-start rounded-xl px-2 py-1 text-left"
							>
								<span className="block truncate text-sm leading-5 font-semibold">{draft.name}</span>
								<span className="flex items-center gap-1.5 text-[11px] leading-4 text-muted-foreground">
									<span
										className={cn(
											"h-1.5 w-1.5 shrink-0 rounded-full",
											draft.latestVersion ? "bg-emerald-400" : "bg-muted-foreground/60",
										)}
									/>
									<span className="truncate">
										{draft.latestVersion
											? `${isDesktop ? "опубликована " : ""}v${draft.latestVersion.number}`
											: "не опубликован"}
									</span>
									<span aria-hidden>·</span>
									<SaveIndicator isSaving={isSaving} />
								</span>
							</Button>
						</div>

						<div className="flex items-center justify-end gap-1">
							{issuesMenu}
							{viewSwitch}
							<Button
								variant="ghost"
								size="sm"
								className="hidden h-9 rounded-xl lg:inline-flex"
								title="Переменные сценария"
								onClick={() => setDialog("variables")}
							>
								<Braces />
								<span className="hidden xl:inline">Переменные</span>
							</Button>
							{testChatButton}
							<Button
								size="sm"
								className="h-9 rounded-xl px-3.5"
								onClick={() =>
									void whenSaved()
										.then(() => setDialog("publish"))
										.catch((error) => toast.error(error.message))
								}
							>
								<Rocket />
								<span className="hidden sm:inline">Опубликовать</span>
							</Button>
							<MoreMenu onPick={setDialog} hideSettings hideVariables={isDesktop} />
						</div>

						{/* Потоки: отдельной строкой: в одной строке с кнопками они наезжали на шапку. */}
						<div className="col-span-2 flex min-w-0 justify-center">
							<FlowsBar
								flows={draft.flows}
								activeFlowId={activeFlow.id}
								errorsByFlow={countErrorsByFlow(draft, graphIssues.issues)}
								onSelect={selectFlow}
								actions={actions}
							/>
						</div>
					</header>
				)}

				<div className="relative flex min-h-0 flex-1 gap-2 px-2 pb-2">
					<div className="relative min-w-0 flex-1 overflow-hidden rounded-2xl border border-border/60 bg-background">
						{/* Список и холст сменяются перекрёстно вместе со своими панелями. */}
						<LoadSwap loading={false} id={view} fill>
							<>
								{showList ? (
									<FlowList
										draft={draft}
										flowId={activeFlow.id}
										issuesByNode={graphIssues.byNode}
										selectedNodeId={selectedNode?.id ?? null}
										activeNodeIds={chat.activeNodeIds}
										onSelectNode={setSelectedNodeId}
										onAddFromPort={(nodeId, port, type) => void addBlock(type, { nodeId, port })}
									/>
								) : (
									<ScenarioCanvas
										draft={draft}
										flowId={activeFlow.id}
										issuesByNode={graphIssues.byNode}
										selectedNodeId={selectedNode?.id ?? null}
										onSelectNode={setSelectedNodeId}
										actions={actions}
										focusRequest={focusRequest}
										activeNodeIds={chat.activeNodeIds}
										nodeStats={embedded?.nodeStats}
										compact={!isDesktop}
									/>
								)}
								{showList ? (
									<ListToolbar
										draft={draft}
										addBlockHint={addBlockHint}
										onAddBlock={(type) => void addBlock(type)}
										onPickNode={showNode}
									/>
								) : (
									<CanvasToolbar
										draft={draft}
										flowId={activeFlow.id}
										addBlockHint={addBlockHint}
										onAddBlock={(type) => void addBlock(type)}
										onPickNode={showNode}
										onArrange={arrange}
									/>
								)}
							</>
						</LoadSwap>
						{embedded?.canvasStart ? (
							<div
								className={cn(
									FLOATING_PANEL_CLASS,
									"absolute top-3 left-3 z-10 flex items-center p-1",
								)}
							>
								{embedded.canvasStart}
							</div>
						) : null}
					</div>

					<AnimatePresence initial={false}>
						{isDesktop && inspector ? (
							<motion.aside
								key="inspector"
								className={PANEL_CLASS}
								{...panelMotion(INSPECTOR_WIDTH)}
							>
								<div className="h-full" style={{ width: INSPECTOR_WIDTH - 2 }}>
									{/* Другой блок открывается перекрёстным переходом, а не подменой. */}
									<LoadSwap loading={false} id={selectedNode?.id ?? "none"} fill>
										{inspector}
									</LoadSwap>
								</div>
							</motion.aside>
						) : null}
						{isDesktop && testOpen ? (
							<motion.aside
								key="test-chat"
								className={PANEL_CLASS}
								{...panelMotion(TEST_CHAT_WIDTH)}
							>
								<div className="h-full" style={{ width: TEST_CHAT_WIDTH - 2 }}>
									{testChat}
								</div>
							</motion.aside>
						) : null}
					</AnimatePresence>
				</div>

				{isDesktop ? null : (
					<BottomSheet
						open={!!selectedNode}
						onOpenChange={(open) => {
							if (!open) setSelectedNodeId(null)
						}}
						title="Блок сценария"
					>
						{inspector}
					</BottomSheet>
				)}
				{isDesktop ? null : (
					<Drawer open={testOpen} onOpenChange={setTestOpen} repositionInputs={false}>
						<DrawerContent
							aria-describedby={undefined}
							onOpenAutoFocus={(event) => event.preventDefault()}
							className="h-[96dvh] max-h-[96dvh] border-none bg-[#0e1621] p-0"
						>
							{/* TestChatPanel уже рендерит собственную шапку с закрытием: заголовок шторки только для a11y. */}
							<DrawerTitle className="sr-only">Тест сценария</DrawerTitle>
							<div className="flex min-h-0 flex-1 flex-col pb-[var(--app-safe-bottom)]">
								{testChat}
							</div>
						</DrawerContent>
					</Drawer>
				)}

				<FlowTriggersDialog
					flow={triggersOpen ? activeFlow : null}
					onOpenChange={setTriggersOpen}
					onSave={(flowId, triggers) => actions.setFlowTriggers(flowId, triggers)}
				/>
				<VariablesDialog
					open={dialog === "variables"}
					onOpenChange={(open) => setDialog(open ? "variables" : null)}
					draft={draft}
					actions={actions}
				/>
				{embedded ? null : (
					<PublishDialog
						open={dialog === "publish"}
						onOpenChange={(open) => setDialog(open ? "publish" : null)}
						draft={draft}
						isSaving={isSaving}
						onPickNode={showNode}
					/>
				)}
				<VersionsDialog
					open={dialog === "versions"}
					onOpenChange={(open) => setDialog(open ? "versions" : null)}
					draft={draft}
					isSaving={isSaving}
				/>
				<ScenarioSettingsDialog
					open={dialog === "settings"}
					onOpenChange={(open) => setDialog(open ? "settings" : null)}
					draft={draft}
				/>
			</div>
		</ReactFlowProvider>
	)
}
